/**
 * Toggleable required-confirmation sections across the spray flow.
 *
 * Each entry corresponds to a gate the operator sees on one of the spray
 * routes (Prepare / Spray / Cleanup). Agronomist Admins can toggle whether
 * each is required from the Spray Settings tab. Defaults are intentionally
 * conservative — every gate ships as required so existing properties don't
 * lose any guard rails after upgrading.
 *
 * The id is also the key under `required_confirmations.setting_value` in
 * spray_settings.
 */
export const SPRAY_REQUIRED_SECTIONS = [
  {
    id: 'ppe_preparation',
    label: 'PPE confirmation — Preparation phase',
    phase: 'prepare',
    defaultRequired: true,
  },
  {
    id: 'water_quantity',
    label: 'Water quantity confirmation',
    phase: 'prepare',
    defaultRequired: true,
  },
  {
    id: 'preparation_tasks',
    label: 'Preparation tasks checklist',
    phase: 'prepare',
    defaultRequired: true,
  },
  {
    id: 'ppe_spraying',
    label: 'PPE confirmation — Spraying phase',
    phase: 'spray',
    defaultRequired: true,
  },
  {
    id: 'equipment_checks',
    label: 'Equipment "meets requirements" checks (nozzle / tractor / sprayer)',
    phase: 'spray',
    defaultRequired: true,
  },
  {
    id: 'equipment_photos',
    label: 'Equipment photo uploads (nozzle / tractor / sprayer)',
    phase: 'spray',
    defaultRequired: false, // off by default — most properties don't want to enforce this
  },
  {
    id: 'cleanup_tasks',
    label: 'Cleanup tasks checklist',
    phase: 'cleanup',
    defaultRequired: true,
  },
  {
    id: 'leftover_mix',
    label: 'Leftover mix handled confirmation',
    phase: 'cleanup',
    defaultRequired: true,
  },
  {
    id: 'ppe_disposal',
    label: 'PPE & equipment cleaned/disposed confirmation',
    phase: 'cleanup',
    defaultRequired: true,
  },
];

/**
 * Build a defaults map { sectionId: boolean } from the section definitions.
 * Used as the seed value for the AgronomistAdmin toggles and as the fallback
 * when a property has no override saved yet.
 */
export const getDefaultRequiredSections = () =>
  SPRAY_REQUIRED_SECTIONS.reduce((acc, s) => {
    acc[s.id] = s.defaultRequired;
    return acc;
  }, {});

/**
 * Merge a partial override from settings_value over the built-in defaults.
 * Anything the override doesn't mention falls back to the default — that
 * way adding a new section in a future release stays safe.
 *
 * Only keys that exist in SPRAY_REQUIRED_SECTIONS are copied; unknown keys
 * (e.g. stale entries left over from a removed section, or anything an
 * external caller might shove in) are dropped on read. Values are Boolean-
 * coerced so the gate checks behave predictably even if the API returns
 * truthy/falsy non-booleans.
 *
 * @param {Object|null|undefined} override
 * @returns {Object<string, boolean>}
 */
export const mergeRequiredSections = (override) => {
  const defaults = getDefaultRequiredSections();
  if (!override || typeof override !== 'object') return defaults;
  const merged = { ...defaults };
  SPRAY_REQUIRED_SECTIONS.forEach(({ id }) => {
    if (id in override) merged[id] = Boolean(override[id]);
  });
  return merged;
};
